import { Client, IMessage, StompSubscription } from '@stomp/stompjs'
import {
  ConversationMessageBroadcastDTO,
  ConversationPresenceDTO,
  ConversationStatus,
  ConversationSupportStatus,
  ConversationTypingDTO,
  PresenceStatus
} from 'models/conversations'
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'

import constate from 'constate'
import { getConversationSocketUrl } from './conversationSocketUrl'
import { embedSession } from 'utils/embedSession'
import { logger } from 'utils/logging'
import { resolveHeartbeatStrategy } from './heartbeatStrategy'
import { useAuth0 } from 'utils/auth'

const TOPIC_PREFIX = '/topic/conversations/'
const USER_TOPIC_PREFIX = '/topic/users/'
const USER_TOPIC_SUFFIX = '/conversations'
const HEARTBEAT_MS = 10_000
const RECONNECT_DELAY_MS = 5_000
const MAX_RECONNECT_DELAY_MS = 5 * 60_000
const REJECTION_STORM_THRESHOLD = 3
const STABLE_SESSION_MS = 30_000
const CLOSE_REPORT_INTERVAL_MS = 10 * 60_000

/** Handler invoked for every real-time message broadcast on a subscribed conversation. */
export type ConversationMessageHandler = (message: ConversationMessageBroadcastDTO) => void

/** Handler invoked when another participant moves their read cursor in a subscribed conversation. */
export type ConversationReadReceiptHandler = (receipt: ConversationReadReceiptDTO) => void

/** Event pushed on the user's personal topic when a conversation they participate in is created. */
export interface ConversationCreatedEvent {
  conversationId: string
}

/** Handler invoked when a brand-new conversation involving the current user is created. */
export type ConversationCreatedHandler = (event: ConversationCreatedEvent) => void

export type ConversationUpdatedEvent = {
  conversationId: string
  supportStatus: ConversationSupportStatus | null
  supportStatusChangedAt?: string | null
  status?: ConversationStatus
  reopenedAt?: string
}

export type ConversationUpdatedHandler = (event: ConversationUpdatedEvent) => void

/** Handler invoked when a participant starts/stops typing in a subscribed conversation. */
export type ConversationTypingHandler = (typing: ConversationTypingDTO) => void

const isPresenceEvent = (payload: Record<string, unknown>): payload is ConversationPresenceDTO =>
  typeof payload.status === 'string' && typeof payload.userId === 'string' && payload.messageId === undefined

const isMessageBroadcast = (payload: Record<string, unknown>): payload is ConversationMessageBroadcastDTO =>
  typeof payload.messageId === 'string' && typeof payload.content === 'string' && payload.lastReadMessageId === undefined

// Backend serializes `type` with @JsonIgnore so we duck-type by the discriminating fields. Read
// receipts uniquely carry `lastReadMessageId` (without `messageId` / `content`).
const isReadReceipt = (payload: Record<string, unknown>): payload is ConversationReadReceiptDTO =>
  typeof payload.lastReadMessageId === 'string' && typeof payload.userId === 'string' && payload.content === undefined

// Typing updates uniquely carry a boolean `isTyping`.
const isTypingEvent = (payload: Record<string, unknown>): payload is ConversationTypingDTO =>
  typeof payload.isTyping === 'boolean' && typeof payload.userId === 'string'

const isConversationStatus = (value: unknown): value is ConversationStatus =>
  typeof value === 'string' && (Object.values(ConversationStatus) as string[]).includes(value)

const toConversationUpdatedEvent = (payload: Record<string, unknown>): ConversationUpdatedEvent | undefined =>
  typeof payload.conversationId === 'string' &&
  'supportStatus' in payload &&
  payload.messageId === undefined &&
  payload.lastReadMessageId === undefined &&
  payload.userId === undefined
    ? {
      conversationId: payload.conversationId,
      supportStatus: (payload.supportStatus as ConversationSupportStatus | null) ?? null,
      ...('supportStatusChangedAt' in payload && {
        supportStatusChangedAt: typeof payload.supportStatusChangedAt === 'string' ? payload.supportStatusChangedAt : null,
      }),
      ...(isConversationStatus(payload.status) && { status: payload.status }),
      ...(typeof payload.reopenedAt === 'string' && { reopenedAt: payload.reopenedAt }),
    }
    : undefined

// Conversation-created events carry only `conversationId` — no message/receipt/presence/typing fields.
const isConversationCreated = (payload: Record<string, unknown>): payload is ConversationCreatedEvent =>
  typeof payload.conversationId === 'string' &&
  payload.messageId === undefined &&
  payload.lastReadMessageId === undefined &&
  payload.userId === undefined

/**
 * Owns the single app-wide STOMP connection to the conversation domain and exposes:
 *  - `presence`: latest known online/offline state per user id (best-effort, populated by WS events).
 *  - `subscribeTo`: declaratively subscribe to a set of conversation topics.
 *  - `registerMessageHandler`: receive every new-message broadcast (used to patch the list cache).
 *
 * Authentication uses the STOMP CONNECT-frame `Authorization` header (browsers cannot set HTTP
 * headers on a native WebSocket handshake) — matching the backend `WebSocketAuthChannelInterceptor`.
 */
const useConversationSocketState = () => {
  const { isAuthenticated, getTokenSilently } = useAuth0()

  // The Propstack embed bridge makes useAuth0 report an authenticated CLIENT, but there is no
  // Auth0 SPA session behind it — getTokenSilently fails ("Login required") on every reconnect
  // attempt, spamming the console with STOMP errors (ISSUE-2373). Conversations are not a
  // feature of the embedded purchase flow, so keep the socket closed while the embed session lives.
  const isEmbedSessionActive = useSyncExternalStore(embedSession.subscribe, embedSession.isActive)

  // The Auth0 provider hands out a new getTokenSilently on each of its renders; reading it through a ref
  // keeps those renders from tearing the socket down and reconnecting.
  const getTokenSilentlyRef = useRef(getTokenSilently)
  getTokenSilentlyRef.current = getTokenSilently

  const clientRef = useRef<Client | null>(null)
  const subscriptionsRef = useRef<Map<string, StompSubscription>>(new Map())
  const userChannelRef = useRef<{ userId: string; subscription: StompSubscription } | null>(null)
  const messageHandlersRef = useRef<Set<ConversationMessageHandler>>(new Set())
  const readReceiptHandlersRef = useRef<Set<ConversationReadReceiptHandler>>(new Set())
  const conversationCreatedHandlersRef = useRef<Set<ConversationCreatedHandler>>(new Set())
  const conversationUpdatedHandlersRef = useRef<Set<ConversationUpdatedHandler>>(new Set())
  const typingHandlersRef = useRef<Set<ConversationTypingHandler>>(new Set())
  const lastCloseReportAtRef = useRef<number | undefined>(undefined)
  const suppressedClosesRef = useRef(0)

  const [isConnected, setIsConnected] = useState(false)
  // The caller's internal UUID, echoed by the backend on the CONNECTED frame (`user-id` header).
  // This is the canonical id the backend stamps on `senderId` / `participants[].userId`, available
  // the instant the socket opens — no dependency on `/user/me` carrying `internalId`.
  const [socketUserId, setSocketUserId] = useState<string | undefined>(undefined)
  const [presence, setPresence] = useState<Record<string, PresenceStatus>>({})

  const handleTopicMessage = useCallback((frame: IMessage) => {
    let payload: Record<string, unknown>

    try {
      payload = JSON.parse(frame.body)
    } catch (error) {
      logger.error('Failed to parse conversation socket payload', error, {
        context: { component: 'ConversationSocketProvider', action: 'handleTopicMessage' },
      })
      return
    }

    if (isPresenceEvent(payload)) {
      setPresence((prev) => ({ ...prev, [payload.userId]: payload.status }))
      return
    }

    if (isMessageBroadcast(payload)) {
      messageHandlersRef.current.forEach((handler) => handler(payload))
      return
    }

    if (isTypingEvent(payload)) {
      typingHandlersRef.current.forEach((handler) => handler(payload))
      return
    }

    const updated = toConversationUpdatedEvent(payload)
    if (updated) {
      conversationUpdatedHandlersRef.current.forEach((handler) => handler(updated))
      return
    }

    if (isConversationCreated(payload)) {
      conversationCreatedHandlersRef.current.forEach((handler) => handler(payload))
      return
    }

    if (isReadReceipt(payload)) {
      readReceiptHandlersRef.current.forEach((handler) => handler(payload))
    }
  }, [])

  // Establish the connection once the user is authenticated; tear it down on sign-out/unmount.
  useEffect(() => {
    if (!isAuthenticated || isEmbedSessionActive) return

    // The broker answers a rejected CONNECT or SUBSCRIBE with an ERROR frame and closes the whole session.
    // stompjs only re-reads reconnectDelay on activate() and on CONNECTED, so a CONNECT rejected on every
    // attempt would reconnect every RECONNECT_DELAY_MS forever (QA-384). We own the backoff instead:
    // deactivate on a rejected close and activate() again once the delay has elapsed.
    let sessionConnected = false
    let sessionRejected = false
    let consecutiveRejections = 0
    let backoffMs = RECONNECT_DELAY_MS
    let backoffTimer: ReturnType<typeof setTimeout> | undefined
    let connectedAt = 0
    let disposed = false
    let heartbeatLost = false
    let webSocketError = false
    const heartbeatStrategy = resolveHeartbeatStrategy()

    const reportUnexpectedClose = (event: CloseEvent) => {
      const now = Date.now()
      const lastReportAt = lastCloseReportAtRef.current
      if (lastReportAt !== undefined && now - lastReportAt < CLOSE_REPORT_INTERVAL_MS) {
        suppressedClosesRef.current += 1
        return
      }
      logger.warn('Conversation socket closed unexpectedly', undefined, {
        context: {
          component: 'ConversationSocketProvider',
          action: 'socketClosed',
          metadata: {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            sessionMs: sessionConnected ? now - connectedAt : null,
            visibilityState: document.visibilityState,
            heartbeatStrategy,
            heartbeatLost,
            webSocketError,
            suppressedCloses: suppressedClosesRef.current,
          },
        },
      })
      lastCloseReportAtRef.current = now
      suppressedClosesRef.current = 0
    }

    const client = new Client({
      brokerURL: getConversationSocketUrl(),
      reconnectDelay: RECONNECT_DELAY_MS,
      heartbeatIncoming: HEARTBEAT_MS,
      heartbeatOutgoing: HEARTBEAT_MS,
      heartbeatStrategy,
      beforeConnect: async () => {
        sessionConnected = false
        sessionRejected = false
        heartbeatLost = false
        webSocketError = false
        try {
          const token = await getTokenSilentlyRef.current()
          client.connectHeaders = token ? { Authorization: `Bearer ${token}` } : {}
        } catch (error) {
          logger.error('Failed to resolve token for conversation socket', error, {
            context: { component: 'ConversationSocketProvider', action: 'beforeConnect' },
          })
        }
      },
      onConnect: (frame) => {
        sessionConnected = true
        connectedAt = Date.now()
        setIsConnected(true)
        const headerUserId = frame.headers['user-id']
        if (headerUserId) setSocketUserId(headerUserId)
      },
      onHeartbeatLost: () => {
        heartbeatLost = true
      },
      onWebSocketError: () => {
        webSocketError = true
      },
      onWebSocketClose: (event: CloseEvent) => {
        if (!sessionRejected && !disposed) reportUnexpectedClose(event)
        if (sessionRejected) {
          // Called before stompjs schedules its own reconnect, which deactivating here suppresses.
          void client.deactivate()
          if (!disposed) {
            backoffTimer = setTimeout(() => {
              if (!disposed) client.activate()
            }, backoffMs)
          }
        } else if (sessionConnected) {
          consecutiveRejections = 0
        }
        setIsConnected(false)
        // Subscription objects belong to the now-closed STOMP session; drop them so the
        // re-subscribe effects (keyed on `isConnected`) re-create them on the next CONNECT.
        subscriptionsRef.current.forEach((subscription) => subscription.unsubscribe())
        subscriptionsRef.current.clear()
        userChannelRef.current = null
      },
      onStompError: (frame) => {
        sessionRejected = true
        if (sessionConnected && Date.now() - connectedAt >= Math.max(backoffMs, STABLE_SESSION_MS)) {
          consecutiveRejections = 0
        }
        consecutiveRejections += 1
        backoffMs = Math.min(RECONNECT_DELAY_MS * 2 ** (consecutiveRejections - 1), MAX_RECONNECT_DELAY_MS)

        const serverMessage = frame.headers.message ?? 'STOMP error'
        if (consecutiveRejections === 1) {
          logger.error('Conversation socket STOMP error', new Error(serverMessage), {
            context: { component: 'ConversationSocketProvider', action: 'onStompError' },
          })
        } else if (consecutiveRejections === REJECTION_STORM_THRESHOLD) {
          logger.warn('Conversation socket rejected on every reconnect, backing off', new Error(serverMessage), {
            context: {
              component: 'ConversationSocketProvider',
              action: 'rejectionStorm',
              metadata: { consecutiveRejections, serverMessage, reconnectDelayMs: backoffMs },
            },
          })
        }
      },
    })

    clientRef.current = client
    client.activate()

    // The ref object is stable across renders; capturing it here is equivalent to reading
    // `subscriptionsRef.current` at teardown and keeps the exhaustive-deps lint rule satisfied.
    const subscriptions = subscriptionsRef.current

    return () => {
      disposed = true
      clearTimeout(backoffTimer)
      subscriptions.forEach((subscription) => subscription.unsubscribe())
      subscriptions.clear()
      userChannelRef.current?.subscription.unsubscribe()
      userChannelRef.current = null
      void client.deactivate()
      clientRef.current = null
      setIsConnected(false)
    }
  }, [isAuthenticated, isEmbedSessionActive])

  /** Subscribe to the current user's personal topic to learn about brand-new conversations. */
  const subscribeToUserChannel = useCallback((userId: string | undefined) => {
    const client = clientRef.current
    if (!client?.connected || !userId) return
    if (userChannelRef.current?.userId === userId) return

    userChannelRef.current?.subscription.unsubscribe()
    const subscription = client.subscribe(`${USER_TOPIC_PREFIX}${userId}${USER_TOPIC_SUFFIX}`, handleTopicMessage)
    userChannelRef.current = { userId, subscription }
  }, [handleTopicMessage])

  /**
   * Additively subscribe to a single conversation topic WITHOUT disturbing existing subscriptions.
   * Used to start receiving a brand-new conversation's messages the instant its creation event lands —
   * bridging the gap until the list refetch re-derives the full subscription set via `subscribeTo`,
   * so the conversation's very first message broadcast isn't missed.
   */
  const subscribeToConversation = useCallback((conversationId: string) => {
    const client = clientRef.current
    if (!client?.connected || subscriptionsRef.current.has(conversationId)) return
    const subscription = client.subscribe(`${TOPIC_PREFIX}${conversationId}`, handleTopicMessage)
    subscriptionsRef.current.set(conversationId, subscription)
  }, [handleTopicMessage])

  /** Declaratively subscribe to exactly the given conversation topics (diffing against current subs). */
  const subscribeTo = useCallback((conversationIds: string[]) => {
    const client = clientRef.current
    if (!client?.connected) return

    const desired = new Set(conversationIds)

    // Unsubscribe from topics no longer in the desired set.
    subscriptionsRef.current.forEach((subscription, id) => {
      if (!desired.has(id)) {
        subscription.unsubscribe()
        subscriptionsRef.current.delete(id)
      }
    })

    // Subscribe to newly-desired topics.
    desired.forEach((id) => {
      if (subscriptionsRef.current.has(id)) return
      const subscription = client.subscribe(`${TOPIC_PREFIX}${id}`, handleTopicMessage)
      subscriptionsRef.current.set(id, subscription)
    })
  }, [handleTopicMessage])

  /** Register a handler for new-message broadcasts. Returns an unregister function. */
  const registerMessageHandler = useCallback((handler: ConversationMessageHandler) => {
    messageHandlersRef.current.add(handler)
    return () => {
      messageHandlersRef.current.delete(handler)
    }
  }, [])

  /** Register a handler for read-receipt broadcasts. Returns an unregister function. */
  const registerReadReceiptHandler = useCallback((handler: ConversationReadReceiptHandler) => {
    readReceiptHandlersRef.current.add(handler)
    return () => {
      readReceiptHandlersRef.current.delete(handler)
    }
  }, [])

  /** Register a handler for conversation-created events. Returns an unregister function. */
  const registerConversationCreatedHandler = useCallback((handler: ConversationCreatedHandler) => {
    conversationCreatedHandlersRef.current.add(handler)
    return () => {
      conversationCreatedHandlersRef.current.delete(handler)
    }
  }, [])

  const registerConversationUpdatedHandler = useCallback((handler: ConversationUpdatedHandler) => {
    conversationUpdatedHandlersRef.current.add(handler)
    return () => {
      conversationUpdatedHandlersRef.current.delete(handler)
    }
  }, [])

  /** Register a handler for typing updates. Returns an unregister function. */
  const registerTypingHandler = useCallback((handler: ConversationTypingHandler) => {
    typingHandlersRef.current.add(handler)
    return () => {
      typingHandlersRef.current.delete(handler)
    }
  }, [])

  /**
   * Notify the backend that the caller started/stopped typing in [conversationId]. The backend
   * auto-stops after its own inactivity timeout, so callers only need to (re)send `true` while typing.
   */
  const sendTyping = useCallback((conversationId: string, isTyping: boolean) => {
    const client = clientRef.current
    if (!client?.connected) return
    client.publish({
      destination: isTyping ? '/app/conversation.typing.start' : '/app/conversation.typing.stop',
      body: JSON.stringify({ conversationId }),
    })
  }, [])

  return {
    isConnected,
    socketUserId,
    presence,
    subscribeTo,
    subscribeToConversation,
    subscribeToUserChannel,
    registerMessageHandler,
    registerReadReceiptHandler,
    registerConversationCreatedHandler,
    registerConversationUpdatedHandler,
    registerTypingHandler,
    sendTyping,
  }
}

export const [ConversationSocketProvider, useConversationSocket] = constate(useConversationSocketState)
